/**
 * Board Reorder — 서버 측 sortOrder 계산 및 충돌 해결 테스트
 */

// --- optimalMidValue (서버 board-mutation.ts와 동일) ---

function optimalMidValue(prev: number, next: number): number {
  if (prev >= next) return prev - 1

  const mid = (prev + next) / 2
  for (let p = 0; p <= 10; p++) {
    const scale = 10 ** p
    const low = Math.floor(prev * scale) + 1
    const high = Math.ceil(next * scale) - 1
    if (low <= high) {
      return Math.min(Math.max(Math.round(mid * scale), low), high) / scale
    }
  }
  return mid
}

// --- optimalMidValue 단위 테스트 ---

describe('optimalMidValue', () => {
  test('정수 간격이 있으면 정수 반환', () => {
    expect(optimalMidValue(1, 3)).toBe(2)
    expect(optimalMidValue(0, 2)).toBe(1)
    expect(optimalMidValue(10, 20)).toBe(15)
  })

  test('정수 간격이 없으면 소수 1자리', () => {
    expect(optimalMidValue(1, 2)).toBe(1.5)
    expect(optimalMidValue(3, 4)).toBe(3.5)
  })

  test('소수 1자리 간격에서 중간값', () => {
    expect(optimalMidValue(1.3, 1.7)).toBe(1.5)
    expect(optimalMidValue(1.1, 1.9)).toBe(1.5)
  })

  test('소수 1자리 인접 → 소수 2자리', () => {
    expect(optimalMidValue(1.3, 1.4)).toBe(1.35)
    expect(optimalMidValue(2.1, 2.2)).toBe(2.15)
  })

  test('결과값이 prev와 next 사이 (open interval)', () => {
    const cases = [[1, 2], [1, 3], [1.3, 1.7], [1.3, 1.4], [0, 100], [-5, -3], [-1, 1]]
    for (const [prev, next] of cases) {
      const result = optimalMidValue(prev, next)
      expect(result).toBeGreaterThan(prev)
      expect(result).toBeLessThan(next)
    }
  })

  test('prev == next → prev - 1', () => {
    expect(optimalMidValue(5, 5)).toBe(4)
    expect(optimalMidValue(0, 0)).toBe(-1)
  })

  test('prev > next (역전) → prev - 1', () => {
    expect(optimalMidValue(5, 3)).toBe(4)
    expect(optimalMidValue(10, 1)).toBe(9)
  })

  test('음수 범위', () => {
    expect(optimalMidValue(-3, -1)).toBe(-2)
    expect(optimalMidValue(-10, -5)).toBe(-7)
  })

  test('0을 포함하는 범위', () => {
    expect(optimalMidValue(-1, 1)).toBe(0)
    expect(optimalMidValue(-0.5, 0.5)).toBe(0)
  })

  test('매우 큰 간격', () => {
    const result = optimalMidValue(0, 10000)
    expect(result).toBe(5000)
    expect(Number.isInteger(result)).toBe(true)
  })

  test('매우 작은 간격 (소수 3자리)', () => {
    const result = optimalMidValue(1.31, 1.32)
    expect(result).toBe(1.315)
    expect(result).toBeGreaterThan(1.31)
    expect(result).toBeLessThan(1.32)
  })

  test('반복 삽입해도 자릿수 제한 (10회)', () => {
    let prev = 1
    let next = 2

    for (let i = 0; i < 10; i++) {
      const mid = optimalMidValue(prev, next)
      expect(mid).toBeGreaterThan(prev)
      expect(mid).toBeLessThan(next)
      const decimalPlaces = (mid.toString().split('.')[1] || '').length
      expect(decimalPlaces).toBeLessThanOrEqual(4)
      next = mid
    }
  })

  test('왼쪽에 반복 삽입 (prev 쪽)', () => {
    let prev = 1
    let next = 2

    for (let i = 0; i < 5; i++) {
      const mid = optimalMidValue(prev, next)
      expect(mid).toBeGreaterThan(prev)
      expect(mid).toBeLessThan(next)
      next = mid
    }
  })

  test('오른쪽에 반복 삽입 (next 쪽)', () => {
    let prev = 1
    let next = 2

    for (let i = 0; i < 5; i++) {
      const mid = optimalMidValue(prev, next)
      expect(mid).toBeGreaterThan(prev)
      expect(mid).toBeLessThan(next)
      prev = mid
    }
  })
})

// --- reorderBoard 서버 로직 시뮬레이션 ---

describe('reorderBoard 서버 로직', () => {
  type Board = { id: string; sortOrder: number | null; name?: string }

  function simulateReorderBoard(boards: Board[], movedId: string, prevId?: string, nextId?: string) {
    const prevBoard = prevId ? boards.find(b => b.id === prevId) : null
    const nextBoard = nextId ? boards.find(b => b.id === nextId) : null

    let prevOrder = prevBoard?.sortOrder ?? (nextBoard?.sortOrder != null ? nextBoard.sortOrder - 2 : 0)
    let nextOrder = nextBoard?.sortOrder ?? (prevOrder != null ? prevOrder + 2 : 2)

    // null이면 기본값으로 대체
    if (prevOrder == null) prevOrder = 0
    if (nextOrder == null) nextOrder = prevOrder + 2

    // 간격이 너무 좁으면 전체 정규화
    if (nextOrder - prevOrder < 0.001) {
      boards.sort((a, b) => {
        const sa = a.sortOrder ?? Number.MAX_SAFE_INTEGER
        const sb = b.sortOrder ?? Number.MAX_SAFE_INTEGER
        if (sa !== sb) return sa - sb
        return (a.name || a.id).localeCompare(b.name || b.id)
      })
      boards.forEach((b, i) => { b.sortOrder = i })

      if (prevBoard) prevOrder = prevBoard.sortOrder!
      if (nextBoard) {
        nextOrder = nextBoard.sortOrder!
      } else {
        nextOrder = prevOrder + 2
      }
    }

    const sortOrder = optimalMidValue(prevOrder, nextOrder)
    const moved = boards.find(b => b.id === movedId)!
    moved.sortOrder = sortOrder

    return boards.sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER))
  }

  // --- 기본 이동 ---

  test('중간으로 이동', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 5 },
      { id: 'C', sortOrder: 3 }
    ]
    simulateReorderBoard(boards, 'B', 'A', 'C')
    expect(boards.map(b => b.id)).toEqual(['A', 'B', 'C'])
    expect(boards[1].sortOrder).toBe(2)
  })

  test('맨 앞으로 이동 (prevId 없음)', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 2 },
      { id: 'C', sortOrder: 3 }
    ]
    simulateReorderBoard(boards, 'C', undefined, 'A')
    expect(boards[0].id).toBe('C')
    expect(boards[0].sortOrder!).toBeLessThan(1)
  })

  test('맨 뒤로 이동 (nextId 없음)', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 2 },
      { id: 'C', sortOrder: 3 }
    ]
    simulateReorderBoard(boards, 'A', 'C', undefined)
    expect(boards[boards.length - 1].id).toBe('A')
    expect(boards[boards.length - 1].sortOrder!).toBeGreaterThan(3)
  })

  // --- 인접 이동 ---

  test('바로 옆으로 이동 (A,B → B,A)', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 2 },
      { id: 'C', sortOrder: 3 }
    ]
    simulateReorderBoard(boards, 'B', undefined, 'A')
    expect(boards[0].id).toBe('B')
    expect(boards[0].sortOrder!).toBeLessThan(1)
  })

  test('인접한 두 보드 사이에 삽입', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 2 },
      { id: 'C', sortOrder: 3 }
    ]
    simulateReorderBoard(boards, 'C', 'A', 'B')
    expect(boards.map(b => b.id)).toEqual(['A', 'C', 'B'])
    expect(boards[1].sortOrder).toBe(1.5)
  })

  // --- sortOrder 충돌/정규화 ---

  test('prev와 next의 sortOrder가 같은 경우 → 정규화', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 5 },
      { id: 'B', sortOrder: 5 },
      { id: 'C', sortOrder: 10 }
    ]
    simulateReorderBoard(boards, 'C', 'A', 'B')

    // 정규화 후 A=0, B=1, C=2 → C가 A(0)와 B(1) 사이 → 0.5
    const sorted = boards.sort((a, b) => a.sortOrder! - b.sortOrder!)
    expect(sorted.every((b, i) => i === 0 || sorted[i - 1].sortOrder! <= b.sortOrder!)).toBe(true)
  })

  test('모든 보드가 같은 sortOrder → 정규화', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 0 },
      { id: 'B', sortOrder: 0 },
      { id: 'C', sortOrder: 0 },
      { id: 'D', sortOrder: 0 }
    ]
    simulateReorderBoard(boards, 'D', 'A', 'B')

    const sorted = boards.sort((a, b) => a.sortOrder! - b.sortOrder!)
    // 정규화 후 모든 보드가 서로 다른 sortOrder를 가짐
    const uniqueOrders = new Set(sorted.map(b => b.sortOrder))
    expect(uniqueOrders.size).toBe(4)
  })

  test('역전된 sortOrder → 정규화', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 10 },
      { id: 'B', sortOrder: 5 },
      { id: 'C', sortOrder: 1 }
    ]
    simulateReorderBoard(boards, 'A', 'C', 'B')

    // 정규화 후 정상 순서
    const sorted = boards.sort((a, b) => a.sortOrder! - b.sortOrder!)
    expect(sorted.every((b, i) => i === 0 || sorted[i - 1].sortOrder! <= b.sortOrder!)).toBe(true)
  })

  // --- NULL sortOrder ---

  test('NULL sortOrder 보드가 포함된 목록', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: null },
      { id: 'C', sortOrder: 2 }
    ]
    // A를 C 뒤로 이동 (nextId=B는 null)
    // nextOrder = null → prevOrder + 2 = 4 → 정상 간격
    simulateReorderBoard(boards, 'A', 'C', undefined)

    expect(boards.find(b => b.id === 'A')!.sortOrder!).toBeGreaterThan(2)
  })

  test('모두 NULL → 정규화 트리거', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: null, name: 'Alpha' },
      { id: 'B', sortOrder: null, name: 'Beta' },
      { id: 'C', sortOrder: null, name: 'Gamma' }
    ]
    // prevId=A(null), nextId=B(null) → prevOrder=0, nextOrder=2 (기본값)
    // 간격 2 > 0.001 → 정규화 안 됨, 그냥 중간값 계산
    simulateReorderBoard(boards, 'C', 'A', 'B')

    // C에 sortOrder가 부여됨
    expect(boards.find(b => b.id === 'C')!.sortOrder).not.toBeNull()
  })

  test('NULL과 정상값 혼재 — 정규화 시 NULL은 맨 뒤', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 5 },
      { id: 'B', sortOrder: 5 },
      { id: 'C', sortOrder: null },
      { id: 'D', sortOrder: null }
    ]
    // A(5)와 B(5) 사이: 간격 0 → 정규화 트리거
    simulateReorderBoard(boards, 'D', 'A', 'B')

    // 정규화 후: A, B는 앞쪽, C/D(null→맨뒤)는 뒤쪽에 배치된 후 정규화
    const sorted = boards.sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER))
    // 모든 보드에 sortOrder가 부여됨 (정규화로 null 제거)
    const nonNull = sorted.filter(b => b.sortOrder != null)
    expect(nonNull.length).toBeGreaterThanOrEqual(3) // 최소 정규화된 보드 + moved
  })

  // --- 연속 재정렬 ---

  test('5회 연속 재정렬 후 순서 유지', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 2 },
      { id: 'C', sortOrder: 3 },
      { id: 'D', sortOrder: 4 },
      { id: 'E', sortOrder: 5 }
    ]

    simulateReorderBoard(boards, 'D', 'A', 'B') // A, D, B, C, E
    simulateReorderBoard(boards, 'E', 'A', 'D') // A, E, D, B, C

    const order = boards.map(b => b.id)
    expect(order.indexOf('A')).toBeLessThan(order.indexOf('E'))
    expect(order.indexOf('E')).toBeLessThan(order.indexOf('D'))
    expect(order.indexOf('D')).toBeLessThan(order.indexOf('B'))
  })

  test('같은 위치에 반복 삽입', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 0 },
      { id: 'B', sortOrder: 10 },
      { id: 'C', sortOrder: 20 },
      { id: 'D', sortOrder: 30 },
      { id: 'E', sortOrder: 40 }
    ]

    // A와 B 사이에 C, D, E를 차례로 삽입
    simulateReorderBoard(boards, 'C', 'A', 'B')
    simulateReorderBoard(boards, 'D', 'A', 'C')
    simulateReorderBoard(boards, 'E', 'A', 'D')

    const order = boards.map(b => b.id)
    expect(order.indexOf('A')).toBeLessThan(order.indexOf('E'))
    expect(order.indexOf('E')).toBeLessThan(order.indexOf('D'))
    expect(order.indexOf('D')).toBeLessThan(order.indexOf('C'))
    expect(order.indexOf('C')).toBeLessThan(order.indexOf('B'))
  })

  test('왕복 이동 (A를 맨뒤로, 다시 맨앞으로)', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 2 },
      { id: 'C', sortOrder: 3 }
    ]

    simulateReorderBoard(boards, 'A', 'C', undefined)
    expect(boards[boards.length - 1].id).toBe('A')

    simulateReorderBoard(boards, 'A', undefined, 'B')
    expect(boards[0].id).toBe('A')
  })

  // --- 보드 2개만 있는 경우 ---

  test('보드 2개 — 순서 교체', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 },
      { id: 'B', sortOrder: 2 }
    ]
    simulateReorderBoard(boards, 'B', undefined, 'A')
    expect(boards[0].id).toBe('B')
    expect(boards[1].id).toBe('A')
  })

  test('보드 1개 — 이동 무의미하지만 에러 없음', () => {
    const boards: Board[] = [
      { id: 'A', sortOrder: 1 }
    ]
    simulateReorderBoard(boards, 'A', undefined, undefined)
    expect(boards[0].id).toBe('A')
  })

  // --- 큰 목록 ---

  test('100개 보드에서 재정렬', () => {
    const boards: Board[] = Array.from({ length: 100 }, (_, i) => ({
      id: `B${i}`,
      sortOrder: i
    }))

    // B99를 B0과 B1 사이로
    simulateReorderBoard(boards, 'B99', 'B0', 'B1')

    const sorted = boards.sort((a, b) => a.sortOrder! - b.sortOrder!)
    const b99Index = sorted.findIndex(b => b.id === 'B99')
    const b0Index = sorted.findIndex(b => b.id === 'B0')
    const b1Index = sorted.findIndex(b => b.id === 'B1')

    expect(b99Index).toBe(b0Index + 1)
    expect(b1Index).toBe(b99Index + 1)
  })
})
